'use client'

import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { usePostHog } from '@posthog/react'
import { useSetAtom } from 'jotai'
import { chatsAtom } from '@/atoms/workspaceAtoms'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { useCodeBlockManager } from '@/hooks/useCodeBlockManager'
import { useAnalytics } from '@/hooks/useAnalytics'
import { ChatUI } from '@/components/chat/chat-ui'
import { useAuth } from '@/context/auth'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import {
  loadChatFromSession,
  saveChatToSession,
  clearChatFromSession,
  clearSessionChats
} from '@/lib/storage/public-chat-storage'
import { clearAllTryTempFileContents } from '@/lib/chat/try-temp-attachments'
import { promoteTryTempAttachments } from '@/lib/chat/promote-try-temp-attachments'
import { buildLoginPath } from '@/lib/auth/redirect-path'
import { safeConsole } from '@/lib/utils/safeConsole'
import type { ChatMessage, ChatSettings } from '@/types'
import { useChatHandler } from '@/components/chat/chat-hooks/use-chat-handler'
import { migratePublicChatToAuthenticated } from '@/components/chat/chat-helpers'

const FIRST_RESPONSE_ERROR_MESSAGE =
  "We couldn't get that first response. Please retry."

const FIRST_RESPONSE_INLINE_ERROR_MESSAGE =
  "I couldn't get a response just now — something went wrong on our end. Tap Retry to try again, or just send a new message below."

const PUBLIC_CHAT_DEFAULT_SETTINGS: ChatSettings = {
  model: 'losi-mini',
  prompt: '',
  temperature: 0.7,
  contextLength: 4096,
  includeProfileContext: false,
  includeWorkspaceInstructions: false,
  embeddingsProvider: 'openai',
  reflectMode: false,
  reasoningEffort: 'minimal'
}

function hasAssistantResponse(messages: ChatMessage[]) {
  return messages.some(
    (message) =>
      message.message.role === 'assistant' &&
      // Inline failure notices are assistant-shaped so they render in the
      // transcript, but they must not count as a real model response
      // (response-shown analytics, save CTA, error clearing).
      (message.message.metadata as Record<string, unknown> | null)
        ?.public_error !== true &&
      typeof message.message.content === 'string' &&
      message.message.content.trim().length > 0
  )
}

function createPublicChatMessage(
  role: 'user' | 'assistant',
  content: string,
  sessionChatId: string,
  model: ChatSettings['model'],
  sequenceNumber: number,
  idPrefix: string
): ChatMessage {
  const now = new Date().toISOString()

  return {
    message: {
      id: `${idPrefix}_${Date.now()}`,
      chat_id: sessionChatId,
      role,
      content,
      created_at: now,
      updated_at: now,
      sequence_number: sequenceNumber,
      annotation: null,
      assistant_id: null,
      image_paths: [],
      model,
      user_id: 'public',
      word_count: null,
      metadata: null,
      type: null,
      session_id: null,
      model_id: null,
      provider_id: null,
      thumbs_up: null
    },
    fileItems: []
  }
}

interface HomepageTrendContext {
  query: string
  traffic?: number
  headlines?: string[]
  region?: string
  source?: string
  timeframe?: string
  picture?: string
  pictureSource?: string
  newsItems?: Array<{
    title: string
    snippet?: string
    url?: string
    picture?: string
    source?: string
  }>
}

const TREND_CONTEXT_MAX_AGE_MS = 5 * 60 * 1000

/**
 * Build a rich trend message that mirrors what the mobile app sends when a
 * trend card is tapped. Instead of sending the bare trend name (which gives
 * the model no framing), we prepend a [TREND]{…}[/TREND] payload with the
 * available context and an instruction to search the web + include images,
 * then use "More on this" as the visible message — exactly like mobile's
 * useSendHandler.
 */
function buildTrendMessage(trend: HomepageTrendContext): string {
  // Map web trend fields to the mobile payload shape. Prefer the structured
  // newsItems parsed from the trending feed (real publisher name + article
  // image); only fall back to bare headlines when none are present. Never
  // stamp the feed source ("google_trends") as a per-item source — that is
  // what made the card show "google_trends" instead of the site name.
  const newsItems =
    trend.newsItems && trend.newsItems.length > 0
      ? trend.newsItems.slice(0, 3).map((item) => ({
          title: item.title,
          url: item.url || '',
          source: item.source,
          picture: item.picture
        }))
      : (trend.headlines || []).slice(0, 3).map((title) => ({
          title,
          url: ''
        }))

  const trendPayload = {
    query: trend.query,
    region: trend.region,
    timeframe: trend.timeframe,
    source: trend.source,
    picture: trend.picture,
    pictureSource: trend.pictureSource,
    traffic: trend.traffic,
    newsItems,
    description:
      'Trend data (Search web to know more, include all images in this context in your final result)'
  }

  try {
    return `[TREND]${JSON.stringify(trendPayload)}[/TREND]\n\nMore on this`
  } catch {
    // If serialization somehow fails, fall back to the bare query.
    return trend.query
  }
}

/**
 * Read and consume a stashed homepage trend context from sessionStorage.
 * Returns null when absent, stale, or mismatched with the current query.
 */
function readHomepageTrendContext(query: string): HomepageTrendContext | null {
  if (typeof window === 'undefined') return null
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem('homepage_trend_context')
  } catch {
    return null
  }
  if (!raw) return null

  // Consume once so a later manual query does not reuse stale trend context.
  try {
    sessionStorage.removeItem('homepage_trend_context')
  } catch {
    // Ignore removal failures.
  }

  try {
    const parsed = JSON.parse(raw) as {
      trend?: HomepageTrendContext
      timestamp?: number
    }
    const trend = parsed?.trend
    if (!trend?.query) return null
    if (
      typeof parsed.timestamp === 'number' &&
      Date.now() - parsed.timestamp > TREND_CONTEXT_MAX_AGE_MS
    ) {
      return null
    }
    // Guard against a mismatched stash (e.g. user changed query manually).
    if (trend.query.trim() !== query.trim()) return null
    return trend
  } catch {
    return null
  }
}

export default function PublicChatPage() {
  const { user } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()
  const posthog = usePostHog()
  const { track } = useAnalytics()
  const setAtomChats = useSetAtom(chatsAtom)
  const { profile, setChats, selectedWorkspace, tools, selectedAssistant, getAttachmentStoreKey, getScopedAttachmentBindings } =
    useContext(ChatbotUIContext)
  const {
    chatSettings,
    setChatSettings,
    selectedTools,
    setSelectedTools,
    setSelectedChat,
    getChatStoreKey,
    getScopedChatBindings
  } = useContext(ChatbotUIChatContext)
  const [sessionChatId] = useState<string>(() => {
    if (typeof window === 'undefined') return `public_${Date.now()}`
    // Try to load existing sessionChatId from sessionStorage
    const storedId = sessionStorage.getItem('public_chat_session_id')
    if (storedId) return storedId
    // Generate new ID and store it
    const newId = `public_${Date.now()}`
    sessionStorage.setItem('public_chat_session_id', newId)
    return newId
  })
  const [hasAutoSent, setHasAutoSent] = useState(false)
  const [isSendingFromQuery, setIsSendingFromQuery] = useState(false)
  const [isMigrating, setIsMigrating] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [firstResponseError, setFirstResponseError] = useState<string | null>(null)
  const [hasTrackedSaveCtaView, setHasTrackedSaveCtaView] = useState(false)
  const tempMessageIdRef = useRef<string | null>(null)
  const lastAutoQueryRef = useRef<{
    message: string
    addon?: string
    source?: string
  } | null>(null)
  const autoRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sendPublicQueryRef = useRef<
    ((query: string, addon?: string, source?: string, isAutoRetry?: boolean) => void) | null
  >(null)
  const trackedHandoffKeyRef = useRef<string | null>(null)
  const trackedResponseShownKeyRef = useRef<string | null>(null)
  const publicChatBindings = getScopedChatBindings(getChatStoreKey(sessionChatId))
  const publicChatState = publicChatBindings.getSnapshot()
  const chatMessages = publicChatState.chatMessages
  const isGenerating = publicChatState.isGenerating
  const setChatMessages = publicChatBindings.setChatMessages

  const { handleSendMessage, handleNewChat, handleStopMessage } = useChatHandler({
    chatId: sessionChatId
  })
  const hasCompletedAssistantResponse = useMemo(
    () => hasAssistantResponse(chatMessages),
    [chatMessages]
  )
  const shouldShowSaveCta =
    !user && hasCompletedAssistantResponse && !isGenerating && !isMigrating

  const readPublicChatMessages = useCallback(
    () =>
      getScopedChatBindings(getChatStoreKey(sessionChatId)).getSnapshot()
        .chatMessages,
    [getChatStoreKey, getScopedChatBindings, sessionChatId]
  )
  const hasLatestAssistantResponse = useCallback(
    () =>
      hasAssistantResponse(readPublicChatMessages()) ||
      hasAssistantResponse(loadChatFromSession(sessionChatId) ?? []),
    [readPublicChatMessages, sessionChatId]
  )

  // Surface a failed first response inside the transcript itself. The
  // top retry banner alone is easy to miss, and a silent "thinking" state
  // that just stops reads as a hang — the user must see their message plus
  // a clear error (and a working composer) whatever setup is doing.
  const appendFirstResponseErrorMessage = useCallback(() => {
    const errorMessage = createPublicChatMessage(
      'assistant',
      FIRST_RESPONSE_INLINE_ERROR_MESSAGE,
      sessionChatId,
      chatSettings?.model || 'losi-mini',
      readPublicChatMessages().length,
      'public_error'
    )
    errorMessage.message.metadata = { public_error: true }
    setChatMessages((prev) => [...prev, errorMessage])
  }, [chatSettings?.model, readPublicChatMessages, sessionChatId, setChatMessages])

  const sendPublicQuery = useCallback(
    (decodedQuery: string, addon?: string, source?: string, isAutoRetry = false) => {
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current)
        autoRetryTimeoutRef.current = null
      }
      clearChatFromSession(sessionChatId)
      setFirstResponseError(null)
      setHasTrackedSaveCtaView(false)
      lastAutoQueryRef.current = { message: decodedQuery, addon, source }

      if (source?.startsWith('homepage')) {
        posthog.capture('homepage_try_auto_send_started', {
          message_length: decodedQuery.trim().length,
          source,
          surface: 'public_chat'
        })
      }

      const tempUserMessage = createPublicChatMessage(
        'user',
        decodedQuery,
        sessionChatId,
        chatSettings?.model || 'losi-mini',
        0,
        'temp_user'
      )

      tempMessageIdRef.current = tempUserMessage.message.id
      setChatMessages([tempUserMessage])
      setIsSendingFromQuery(true)
      setHasAutoSent(true)

      // A failed first response gets ONE automatic retry before the user
      // sees any error: transient provider failures (e.g. a Bedrock blip
      // that resolves the send with no assistant content) shouldn't cost
      // a first-time visitor their answer. Only after the retry also
      // fails do we surface the inline error + retry banner.
      const handleFirstResponseFailure = (stage: 'missing_response' | 'send_error') => {
        if (!isAutoRetry) {
          if (source?.startsWith('homepage')) {
            posthog.capture('homepage_try_auto_send_retrying', {
              source,
              stage,
              surface: 'public_chat'
            })
          }
          autoRetryTimeoutRef.current = setTimeout(() => {
            sendPublicQueryRef.current?.(decodedQuery, addon, source, true)
          }, 1200)
          return
        }

        appendFirstResponseErrorMessage()
        setFirstResponseError(FIRST_RESPONSE_ERROR_MESSAGE)
        if (source?.startsWith('homepage')) {
          posthog.capture('homepage_try_auto_send_failed', {
            source,
            stage,
            surface: 'public_chat'
          })
        }
        setIsSendingFromQuery(false)
      }

      void handleSendMessage(decodedQuery, [], false, { addon })
        .then(() => {
          if (!hasLatestAssistantResponse()) {
            handleFirstResponseFailure('missing_response')
          }
        })
        .catch(() => {
          handleFirstResponseFailure('send_error')
        })
    },
    [
      appendFirstResponseErrorMessage,
      chatSettings?.model,
      handleSendMessage,
      hasLatestAssistantResponse,
      posthog,
      sessionChatId,
      setChatMessages
    ]
  )

  // Keep the ref pointed at the latest sendPublicQuery so the delayed
  // auto-retry calls the current closure, and drop any pending retry on
  // unmount so it can't fire into a dead page.
  useEffect(() => {
    sendPublicQueryRef.current = sendPublicQuery
  }, [sendPublicQuery])

  useEffect(() => {
    return () => {
      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current)
      }
    }
  }, [])

  const handleRetryFirstResponse = useCallback(() => {
    const lastAutoQuery = lastAutoQueryRef.current
    if (!lastAutoQuery) return

    sendPublicQuery(lastAutoQuery.message, lastAutoQuery.addon, lastAutoQuery.source)
  }, [sendPublicQuery])

  const handleSaveCtaClick = useCallback(() => {
    const source = searchParams?.get('source') || lastAutoQueryRef.current?.source
    // Dual-track: server route (historical) + client PostHog so homepage →
    // try → signup funnels share the same anonymous person identity.
    track('public_chat_save_cta_clicked', {
      surface: 'try',
      messageCount: chatMessages.length
    })
    posthog.capture('public_chat_save_cta_clicked', {
      message_count: chatMessages.length,
      source: source || 'try',
      surface: 'try'
    })

    if (source?.startsWith('homepage')) {
      posthog.capture('homepage_cta_clicked', {
        message_count: chatMessages.length,
        source,
        surface: 'public_chat',
        target: 'signup'
      })
    }

    router.push(buildLoginPath({ register: true, next: '/try' }))
  }, [chatMessages.length, posthog, router, searchParams, track])

  // New chat from /try: clear the anonymous session and return home with the
  // homepage intake ready to type (compose flag + ?compose=1 focuses hero input).
  const handleNewPublicChat = () => {
    // Abort any in-flight public stream so New does not race a late chunk write.
    if (isGenerating) {
      handleStopMessage({ restoreInput: false })
    }

    clearSessionChats()
    clearAllTryTempFileContents()

    // handleNewChat() returns early without a workspace, so clear try drafts here.
    const attachmentKey = getAttachmentStoreKey(sessionChatId)
    const attachmentBindings = getScopedAttachmentBindings(attachmentKey)
    attachmentBindings.setNewMessageFiles([])
    attachmentBindings.setNewMessageImages([])
    attachmentBindings.setChatFiles([])
    attachmentBindings.setChatImages([])
    attachmentBindings.setShowFilesDisplay(false)

    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem('public_chat_session_id')
        sessionStorage.setItem('losi:homepage-compose', '1')
      } catch {
        // Focus still works via ?compose=1 when sessionStorage is blocked.
      }
    }
    setChatMessages([])
    setHasAutoSent(false)
    setIsSendingFromQuery(false)
    setFirstResponseError(null)
    setHasTrackedSaveCtaView(false)
    lastAutoQueryRef.current = null
    tempMessageIdRef.current = null
    handleNewChat()
    router.push('/?compose=1')
  }

  // Ensure component is mounted on client before rendering
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Load messages from session storage when user becomes authenticated (before migration)
  useEffect(() => {
    if (user && chatMessages.length === 0 && !isMigrating) {
      const savedMessages = loadChatFromSession(sessionChatId)
      if (savedMessages && savedMessages.length > 0) {
        setChatMessages(savedMessages)
      }
    }
  }, [user, chatMessages.length, isMigrating, sessionChatId, setChatMessages])

  // Migrate public chat to authenticated user and redirect
  useEffect(() => {
    // Add a small delay to ensure profile/workspace are loaded after auth
    const timeoutId = setTimeout(() => {
    if (user && profile && selectedWorkspace && chatSettings && !isMigrating) {
      const attachmentKey = getAttachmentStoreKey(sessionChatId)
      const attachmentBindings = getScopedAttachmentBindings(attachmentKey)
      const attachmentSnapshot = attachmentBindings.getSnapshot()

      const promotePendingDrafts = async () => {
        if (
          attachmentSnapshot.newMessageFiles.length === 0 &&
          attachmentSnapshot.newMessageImages.length === 0
        ) {
          return
        }

        const promoted = await promoteTryTempAttachments({
          profile,
          workspaceId: selectedWorkspace.id,
          chatSettings,
          newMessageFiles: attachmentSnapshot.newMessageFiles,
          newMessageImages: attachmentSnapshot.newMessageImages
        })

        attachmentBindings.setNewMessageFiles(promoted.promotedFiles)
        attachmentBindings.setNewMessageImages(promoted.promotedImages)
        if (promoted.promotedFiles.length > 0 || promoted.promotedImages.length > 0) {
          attachmentBindings.setShowFilesDisplay(true)
        }
      }

      // Only migrate if there are messages to save
      if (chatMessages.length > 0) {
        setIsMigrating(true)
        void promotePendingDrafts()
          .catch(error => {
            safeConsole.warn('Try temp attachment promote failed', error)
          })
          .then(() =>
            migratePublicChatToAuthenticated(
              chatMessages,
              chatSettings,
              profile,
              selectedWorkspace,
              selectedAssistant || null
            )
          )
          .then((createdChat) => {
            const chatWithMetadata = { ...createdChat, metadata: null, type: null }
            
            // Update chats list in context
            setChats((chats) => {
              // Check if chat already exists to avoid duplicates
              const existingIndex = chats.findIndex(c => c.id === createdChat.id)
              if (existingIndex >= 0) {
                // Update existing chat
                const updated = [...chats]
                updated[existingIndex] = chatWithMetadata
                return updated
              }
              // Add new chat at the beginning
              return [chatWithMetadata, ...chats]
            })
            
            // Update atomChats so sidebar shows the new chat immediately
            setAtomChats((atomChats) => {
              // Check if chat already exists to avoid duplicates
              const existingIndex = atomChats.findIndex(c => c.id === createdChat.id)
              if (existingIndex >= 0) {
                // Update existing chat
                const updated = [...atomChats]
                updated[existingIndex] = chatWithMetadata
                return updated
              }
              // Add new chat at the beginning (most recent first)
              const newAtomChats = [chatWithMetadata, ...atomChats]
              return newAtomChats
            })
            
            // Set selected chat so it loads immediately after redirect
            setSelectedChat(chatWithMetadata)
            
            // Clear session storage
            clearChatFromSession(sessionChatId)
            clearAllTryTempFileContents()
            // Clear persistent sessionChatId so new public sessions get a fresh ID
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('public_chat_session_id')
            }
            
            // Use a small delay to ensure atomChats update completes before redirect
            // This prevents the sidebar's fetchInitialChats from overwriting our update
            setTimeout(() => {
              // Redirect to the new chat
              router.push(`/chat/${createdChat.id}`)
            }, 100)
          })
          .catch(() => {
            safeConsole.warn('Public chat migration failed')
            setIsMigrating(false)
            // Fallback: redirect to empty chat if migration fails
            router.push('/chat')
          })
      } else {
        // Promote any unsent try drafts even when there is no chat history yet.
        void promotePendingDrafts()
          .then(() => {
            // No messages to migrate: if user came with ?query=, send them to chat with query
            const query = searchParams?.get('query')
            if (query) {
              router.replace(`/chat?query=${encodeURIComponent(query)}&fromTry=1`)
              return
            }
            // If we landed on /try after login/signup (e.g. Google or magic email), session chat
            // may not be in state yet. Try loading from sessionStorage once before redirecting.
            const savedMessages = loadChatFromSession(sessionChatId)
            if (savedMessages && savedMessages.length > 0) {
              setChatMessages(savedMessages)
              return
            }
            router.push('/chat')
          })
          .catch(() => {
            router.push('/chat')
          })
      }
    }
    }, 1000) // Wait 1 second for profile/workspace to load

    return () => clearTimeout(timeoutId)
  }, [user, profile, selectedWorkspace, chatSettings, chatMessages, selectedAssistant, setChats, router, sessionChatId, isMigrating, searchParams, setChatMessages, getAttachmentStoreKey, getScopedAttachmentBindings, setAtomChats, setSelectedChat])

  // Initialize chat settings for public users synchronously (before paint)
  useLayoutEffect(() => {
    if (!user) {
      if (!chatSettings) {
        setChatSettings(PUBLIC_CHAT_DEFAULT_SETTINGS)
      } else if (chatSettings.model !== 'losi-mini') {
        // Force losi-mini if user tries to change model
        setChatSettings({
          ...chatSettings,
          model: 'losi-mini'
        })
      }
    }
  }, [user, chatSettings, setChatSettings])
  
  // Also ensure settings persist in a regular useEffect
  useEffect(() => {
    if (!user && chatSettings && chatSettings.model !== 'losi-mini') {
      setChatSettings({
        ...chatSettings,
        model: 'losi-mini'
      })
    }
  }, [user, chatSettings, setChatSettings])

  useEffect(() => {
    if (hasCompletedAssistantResponse) {
      setFirstResponseError(null)
    }
  }, [hasCompletedAssistantResponse])

  useEffect(() => {
    const query = searchParams?.get('query')?.trim()
    const source = searchParams?.get('source')?.trim()

    if (!query || !source?.startsWith('homepage')) {
      return
    }

    const handoffKey = `${source}:${query}`
    if (trackedHandoffKeyRef.current === handoffKey) {
      return
    }

    trackedHandoffKeyRef.current = handoffKey
    posthog.capture('homepage_try_handoff_received', {
      message_length: query.length,
      source,
      surface: 'public_chat'
    })
  }, [posthog, searchParams])

  useEffect(() => {
    if (!hasCompletedAssistantResponse) {
      return
    }

    const source = searchParams?.get('source') || lastAutoQueryRef.current?.source
    if (!source?.startsWith('homepage')) {
      return
    }

    const message = searchParams?.get('query') || lastAutoQueryRef.current?.message || ''
    const responseKey = `${source}:${message}`
    if (trackedResponseShownKeyRef.current === responseKey) {
      return
    }

    trackedResponseShownKeyRef.current = responseKey
    posthog.capture('homepage_chat_response_shown', {
      message_count: chatMessages.length,
      message_length: message.trim().length,
      source,
      surface: 'public_chat'
    })
  }, [chatMessages.length, hasCompletedAssistantResponse, posthog, searchParams])

  useEffect(() => {
    if (!shouldShowSaveCta || hasTrackedSaveCtaView) {
      return
    }

    const source = searchParams?.get('source') || lastAutoQueryRef.current?.source
    track('public_chat_save_cta_viewed', {
      surface: 'try',
      messageCount: chatMessages.length
    })
    // Client capture keeps the anonymous person continuous with homepage_* events.
    posthog.capture('public_chat_save_cta_viewed', {
      message_count: chatMessages.length,
      source: source || 'try',
      surface: 'try'
    })
    setHasTrackedSaveCtaView(true)
  }, [
    chatMessages.length,
    hasTrackedSaveCtaView,
    posthog,
    searchParams,
    shouldShowSaveCta,
    track
  ])

  // Initialize Web Browsing tool by default for public users
  useEffect(() => {
    if (!user && tools && tools.length > 0) {
      const webBrowsingTool = tools.find(
        (tool) => 
          tool.name === 'Web Browsing' || 
          tool.name === 'Web Browsing Tool' ||
          tool.name === 'web_browsing' ||
          (tool.name?.toLowerCase().includes('web') && tool.name?.toLowerCase().includes('brows'))
      )
      if (webBrowsingTool && selectedTools.length === 0) {
        // Only set if no tools are selected yet to avoid infinite loops
        setSelectedTools([webBrowsingTool])
      }
    }
  }, [user, tools, setSelectedTools]) // Removed selectedTools from deps to avoid loops

  // Reset hasAutoSent when query param changes
  useEffect(() => {
    if (!isMounted) return
    try {
      const query = searchParams?.get('query')
      if (query) {
        setHasAutoSent(false)
        setFirstResponseError(null)
      }
    } catch {
      // searchParams might not be ready during client-side navigation
      safeConsole.warn('Public chat search params unavailable')
    }
  }, [searchParams, isMounted])

  // Handle dive (from search), query parameter, and sessionStorage loading
  // Run for both guest and authenticated so dive context is applied before migration redirect
  useEffect(() => {
    if (!isMounted) return

    try {
      const isDive = searchParams?.get('dive') === '1'
      const query = searchParams?.get('query')

    // Priority 0: "Dive deeper" from search — load context as
    // pre-existing conversation, don't auto-send
    if (isDive && !hasAutoSent && chatMessages.length === 0) {
      const raw = typeof window !== 'undefined'
        ? sessionStorage.getItem('search_dive_context')
        : null
      if (raw) {
        try {
          const ctx = JSON.parse(raw) as {
            query: string
            aiOverview: string
            sources: string
            timestamp: number
          }
          // Only use context if it's recent (< 5 min)
          if (Date.now() - ctx.timestamp < 5 * 60 * 1000) {
            const assistantContent =
              ctx.aiOverview +
              (ctx.sources
                ? `\n\n---\n**Sources:**\n${ctx.sources}`
                : '')
            const model = chatSettings?.model || 'losi-mini'
            const userMsg = createPublicChatMessage(
              'user',
              ctx.query,
              sessionChatId,
              model,
              0,
              'dive_user'
            )
            const assistantMsg = createPublicChatMessage(
              'assistant',
              assistantContent,
              sessionChatId,
              model,
              1,
              'dive_asst'
            )
            setChatMessages([userMsg, assistantMsg])
            setHasAutoSent(true) // Prevent auto-send of query
            sessionStorage.removeItem('search_dive_context')
            return
          }
        } catch {
          // Ignore parse errors
        }
        sessionStorage.removeItem('search_dive_context')
      }
    }

    // Priority 1: Handle query parameter (new conversation from homepage)
    if (query && !hasAutoSent && !isGenerating) {
      const addon = searchParams?.get('addon') || undefined
      const source = searchParams?.get('source') || undefined
      // If this came from a trending chip, build the rich [TREND]{…}[/TREND]
      // payload (matching the mobile app) so the model gets context/news/
      // images instead of a bare trend name. The display path strips the
      // block so the bubble stays clean ("More on this").
      const isTrendHandoff = searchParams?.get('trend') === '1'
      if (isTrendHandoff) {
        const trendContext = readHomepageTrendContext(query)
        if (trendContext) {
          sendPublicQuery(buildTrendMessage(trendContext), addon, source)
          return
        }
      }
      sendPublicQuery(query, addon, source)
      return // Don't load from sessionStorage when starting new conversation
    }
    
    // Priority 2: Load from sessionStorage (only if no query param and not already sent)
    if (!query && !isDive && !hasAutoSent && chatMessages.length === 0) {
      const savedMessages = loadChatFromSession(sessionChatId)
      if (savedMessages && savedMessages.length > 0) {
        setChatMessages(savedMessages)
      }
    }
    } catch {
      // searchParams might not be ready during client-side navigation
      safeConsole.warn('Public chat search params unavailable')
    }
  }, [searchParams, hasAutoSent, isGenerating, chatMessages.length, sessionChatId, setChatMessages, user, isMounted, sendPublicQuery])

  // Clean up temp message when generation starts (handleSendMessage has added its own)
  useEffect(() => {
    if (isGenerating && isSendingFromQuery && tempMessageIdRef.current && chatMessages.length > 1) {
      // Check if there's another user message (handleSendMessage's tempUserChatMessage)
      const hasOtherUserMessage = chatMessages.some(
        (msg) => msg.message.role === 'user' && msg.message.id !== tempMessageIdRef.current
      )
      
      // Only remove our temp message if handleSendMessage has added its own
      if (hasOtherUserMessage) {
        setChatMessages((prev) => prev.filter((msg) => msg.message.id !== tempMessageIdRef.current))
        tempMessageIdRef.current = null
        setIsSendingFromQuery(false)
      }
    }
  }, [isGenerating, isSendingFromQuery, chatMessages, setChatMessages])

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    if (!user && chatMessages.length > 0) {
      saveChatToSession(sessionChatId, chatMessages)
    }
  }, [chatMessages, sessionChatId, user])

  const { handleSelectCodeBlock, handleSelectMessage } = useCodeBlockManager(chatMessages)

  // Show loading state until component is mounted and chat settings are initialized
  // Suspense handles searchParams loading, but we still need to wait for contexts
  if (!isMounted || !chatSettings) {
    return (
      <div className="relative flex size-full items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="relative flex size-full">
      <div className="size-full">
        <ChatUI
          chatId={sessionChatId}
          isPublic={true}
          onSelectCodeBlock={handleSelectCodeBlock}
          onSelectMessage={handleSelectMessage}
          experimentalCodeEditor={false}
          onNewChat={handleNewPublicChat}
          publicHeaderAuthAction={
            shouldShowSaveCta ? (
              <div className="border-border bg-background/95 flex h-10 min-w-0 items-center gap-2 rounded-full border px-1.5 pl-3 shadow-sm backdrop-blur sm:gap-3 sm:pl-4">
                <span className="text-muted-foreground hidden max-w-[28vw] truncate text-sm md:block xl:max-w-sm">
                  Keep this chat and continue in Losi.
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveCtaClick}
                  className="h-8 shrink-0 rounded-full px-3 text-xs font-medium sm:px-4 sm:text-sm"
                >
                  <span className="hidden sm:inline">Start free</span>
                  <span className="sm:hidden">Keep chat</span>
                </Button>
              </div>
            ) : null
          }
          publicInlineSaveCta={
            shouldShowSaveCta ? (
              <div className="border-border bg-background flex w-full flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5">
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold tracking-tight">
                    Keep going where this left off.
                  </p>
                  <p className="text-muted-foreground mt-1 text-sm leading-6">
                    Create a free account to save this chat and continue later. Google, magic
                    link, or email work.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleSaveCtaClick}
                  className="h-11 shrink-0 rounded-full px-5 text-sm font-medium"
                >
                  Start free
                </Button>
              </div>
            ) : null
          }
        />
      </div>

      {firstResponseError ? (
        <div className="pointer-events-none absolute inset-x-3 bottom-24 z-30 flex justify-center sm:bottom-28">
          <div className="border-destructive/30 bg-background/95 text-foreground pointer-events-auto flex w-full max-w-2xl flex-col gap-3 rounded-xl border p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">{firstResponseError}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleRetryFirstResponse}
              className="w-full shrink-0 sm:w-auto"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

    </div>
  )
}
